import * as THREE from "three";
import type { AttachmentUse, Motion, SocketName } from "./types";
import type { HqDim } from "../../hqAnchors";
import { expandAt, type SocketFrame } from "./sockets";
import { fitFrame, fitMatrix, fitScale, fitSpecOf, taperAt, type FitDims } from "./fit";
import type { FitSpec } from "./bigTypes";
import { isHidden } from "./hide";

/** One authored prop of a kit GLB, in its own frame (+X right, +Y up, +Z out; origin = its mount point). */
export interface KitPiece {
  readonly name: string;
  /** position, normal, uv, and the cloth weight per vertex (COLOR_0.r; 0 when unauthored). Indexed. */
  readonly geometry: THREE.BufferGeometry;
  readonly material: THREE.MeshStandardMaterial;
  /** The piece's default motion from Blender (`td_motion` custom property, JSON). */
  readonly motion?: Motion;
  readonly tris: number;
}

/** Where a socket's part stands: its bone's index and the matrix taking root (model) space into that bone's space. */
export interface BoneMount { readonly index: number; readonly toLocal: THREE.Matrix4 }

/** The highest tier a use's `tiers` filter and `scale` triple know: tier IV (a branch model) counts as tier 3. */
export const TOP_KIT_TIER = 3;
/** Copies of a piece sharing one orbit (kitData.x = row + copy × CopyStride: the GPU table has ≤ 12 rows). */
export const ORBIT_COPIES_MAX = 8;
export const COPY_STRIDE = 16;

/**
 * The uses of `uses` that apply to tower `tower` at `tier`, each with its row in the kit's motion table and with its
 * `fit` for that tower (if any) applied. `hidden`: the stock groups the kit really hides on this model (kit/hide.ts),
 * for the `ifHidden` / `ifShown` filters.
 */
export function usesFor(uses: readonly AttachmentUse[], tower: string, tier: number, hidden = 0): { use: AttachmentUse; row: number }[] {
  const t = Math.min(TOP_KIT_TIER, tier);
  return uses.map((use, row) => ({ use, row }))
    .filter(({ use }) => (!use.towers || use.towers.includes(tower)) && (!use.tiers || use.tiers.includes(t)))
    .filter(({ use }) => (!use.ifHidden || isHidden(hidden, use.ifHidden)) && (!use.ifShown || !isHidden(hidden, use.ifShown)))
    .map(({ use, row }) => {
      const fit = use.fit?.[tower];
      if (!fit) return { use, row };
      const merged = { ...use, ...fit };
      return { use: (fit.fitTo === null ? { ...merged, fitTo: undefined } : merged) as AttachmentUse, row };
    });
}

/** A use's scale at a tier (a [t1, t2, t3] triple grows with the tier; tier IV takes the t3 value). */
export function scaleAt(use: AttachmentUse, tier: number): number {
  const s = use.scale ?? 1;
  return typeof s === "number" ? s : s[Math.max(0, Math.min(2, tier - 1))];
}

/** The model-space matrix of a piece mounted by `use` on socket `f`: the socket frame, then the use's offset, turn and scale. */
export function mountMatrix(f: SocketFrame, use: AttachmentUse, tier: number): THREE.Matrix4 {
  const basis = new THREE.Matrix4().makeBasis(new THREE.Vector3(...f.right), new THREE.Vector3(...f.up), new THREE.Vector3(...f.out));
  basis.setPosition(...f.p);
  const [ox, oy, oz] = use.offset ?? [0, 0, 0];
  const local = new THREE.Matrix4().makeTranslation(ox, oy, oz)
    .multiply(new THREE.Matrix4().makeRotationY(THREE.MathUtils.degToRad(use.yaw ?? 0)))
    .multiply(new THREE.Matrix4().makeScale(...([1, 1, 1].map(() => scaleAt(use, tier)) as [number, number, number])));
  return basis.multiply(local);
}

/** How many orbiting copies a motion asks for (1 without an orbit). */
export function orbitCopies(m: Motion | undefined): number {
  return m?.orbit ? Math.max(1, Math.min(ORBIT_COPIES_MAX, Math.round(m.orbit.copies ?? 1))) : 1;
}

/** One placed instance of a piece: its model-space matrix, the frame it moves in, its row, copy and taper. */
interface Placed { piece: KitPiece; m: THREE.Matrix4; pm: THREE.Matrix4; mount: BoneMount; f: SocketFrame; row: number; copy: number; taper: { dim: HqDim; spec: FitSpec } | null; reach: number }

/** Every placed instance of `uses` on a model (fitted uses once on their envelope, the rest on their sockets). */
function place(
  uses: readonly { use: AttachmentUse; row: number }[], pieces: ReadonlyMap<string, KitPiece>, frames: Readonly<Record<SocketName, SocketFrame>>,
  mountOf: (part: string) => BoneMount, tier: number, dims: FitDims | undefined,
): Placed[] {
  const out: Placed[] = [];
  for (const { use, row } of uses) {
    const piece = pieces.get(use.piece);
    if (!piece) continue;
    const motion = use.motion ?? piece.motion, copies = orbitCopies(motion), k = scaleAt(use, tier);
    const radius = motion?.orbit?.radius ?? 0;
    // an orbiting piece is built its radius out along its out axis (in the piece's own units: metres on a socket, the
    // unit envelope on a fitted use), and the GPU turns it round its mount point
    const ring = (m: THREE.Matrix4, perUnit: number) => (radius ? m.clone().multiply(new THREE.Matrix4().makeTranslation(0, 0, radius / perUnit)) : m);
    // `pm`: the mount point's matrix (what the piece turns and orbits about), `m` the piece's own (out on its orbit)
    const add = (pm: THREE.Matrix4, m: THREE.Matrix4, f: SocketFrame, taper: Placed["taper"], reach: number) => { for (let c = 0; c < copies; c++) out.push({ piece, m, pm, mount: mountOf(f.part), f, row, copy: c, taper, reach }); };
    if (use.fitTo) {
      const spec = fitSpecOf(use.fitTo), dim = dims?.[spec.to];
      if (!dim) continue;                            // the model has no such part (frost's roof): the use is skipped
      const fm = fitMatrix(dim, spec, k, use.offset, use.yaw ?? 0);
      add(fm, ring(fm, 1), fitFrame(dim, spec), spec.taper ? { dim, spec } : null, radius * fitScale(dim, spec).z * k);
      continue;
    }
    for (const s of expandAt(use.at)) { const f = frames[s]; if (!f) continue; const mm = mountMatrix(f, use, tier); add(mm, ring(mm, k), f, null, radius); }
  }
  return out;
}

/** Triangles a kit puts on a model (orbit copies counted): the budget check's number. */
export function kitTriangles(uses: readonly { use: AttachmentUse; row: number }[], pieces: ReadonlyMap<string, KitPiece>, frames: Readonly<Record<SocketName, SocketFrame>>, tier: number, dims?: FitDims): number {
  const mount = { index: 0, toLocal: new THREE.Matrix4() };
  return place(uses, pieces, frames, () => mount, tier, dims).reduce((n, p) => n + p.piece.tris, 0);
}

/**
 * Merges a kit's attachments for one tower model into one skinned geometry: every vertex of every mounted piece in
 * its socket part's bone space, with the motion attributes (kitMaterial.ts) and its bone (weight 1). Returns null
 * when nothing mounts. `bounds` gets the model-space box, for culling. `dims`: the model's fit envelopes (fit.ts).
 */
export function buildKitGeometry(
  uses: readonly { use: AttachmentUse; row: number }[], pieces: ReadonlyMap<string, KitPiece>, frames: Readonly<Record<SocketName, SocketFrame>>,
  mountOf: (part: string) => BoneMount, tier: number, bounds?: THREE.Box3, dims?: FitDims,
): THREE.BufferGeometry | null {
  const parts = place(uses, pieces, frames, mountOf, tier, dims);
  if (parts.length === 0) return null;
  const nv = parts.reduce((n, p) => n + p.piece.geometry.getAttribute("position").count, 0);
  const ni = parts.reduce((n, p) => n + p.piece.geometry.index!.count, 0);
  const pos = new Float32Array(nv * 3), nrm = new Float32Array(nv * 3), uv = new Float32Array(nv * 2);
  const pivot = new Float32Array(nv * 3), up = new Float32Array(nv * 3), out = new Float32Array(nv * 3), data = new Float32Array(nv * 2);
  const skinIndex = new Uint16Array(nv * 4), skinWeight = new Uint8Array(nv * 4);
  const index = nv > 0xffff ? new Uint32Array(ni) : new Uint16Array(ni);
  const v = new THREE.Vector3(), n = new THREE.Vector3(), n3 = new THREE.Matrix3(), full = new THREE.Matrix4(), toRootOnly = new THREE.Matrix4(), jac = new THREE.Matrix3();
  let base = 0, at = 0;
  for (const p of parts) {
    const g = p.piece.geometry;
    const P = g.getAttribute("position"), N = g.getAttribute("normal"), U = g.getAttribute("uv"), W = g.getAttribute("kitWeight");
    full.multiplyMatrices(p.mount.toLocal, p.m);
    n3.getNormalMatrix(full);
    toRootOnly.copy(p.m);
    // the socket's point and axes in bone space; the axes keep the bone's metre-to-local factor as their length
    const lin = new THREE.Matrix3().setFromMatrix4(p.mount.toLocal);
    // the piece's mount point (the socket point plus the use's offset; an orbit's centre) is what it spins and swings about
    const piv = new THREE.Vector3(0, 0, 0).applyMatrix4(p.mount.toLocal.clone().multiply(p.pm));
    const ax = (a: readonly number[]) => new THREE.Vector3(...a).applyMatrix3(lin);
    const upL = ax(p.f.up), outL = ax(p.f.out);
    // a piece mounted with a yaw turns its own axes: the motion follows the piece, not the socket
    const yawed = new THREE.Matrix3().setFromMatrix4(p.m);
    const pieceUp = new THREE.Vector3(0, 1, 0).applyMatrix3(yawed).normalize().applyMatrix3(lin);
    const pieceOut = new THREE.Vector3(0, 0, 1).applyMatrix3(yawed).normalize().applyMatrix3(lin);
    const useUp = pieceUp.lengthSq() > 0 ? pieceUp : upL, useOut = pieceOut.lengthSq() > 0 ? pieceOut : outL;
    if (bounds && p.reach > 0) {
      // an orbiting piece sweeps a ring of its orbit's radius round its mount point
      const c = new THREE.Vector3(0, 0, 0).applyMatrix4(p.pm);
      bounds.expandByPoint(v.set(c.x - p.reach, c.y - p.reach, c.z - p.reach)).expandByPoint(v.set(c.x + p.reach, c.y + p.reach, c.z + p.reach));
    }
    for (let i = 0; i < P.count; i++) {
      const o = base + i;
      v.fromBufferAttribute(P, i);
      if (N) n.fromBufferAttribute(N, i);
      if (p.taper) {
        // the fitted piece follows its part's taper: x and z scaled by the radius at its height (normals by J⁻ᵀ)
        const y = v.y, k = taperAt(p.taper.dim, p.taper.spec, y), dk = (taperAt(p.taper.dim, p.taper.spec, y + 1e-3) - k) / 1e-3;
        jac.set(k, dk * v.x, 0, 0, 1, 0, 0, dk * v.z, k);
        if (N) n.applyMatrix3(jac.invert().transpose());
        v.x *= k; v.z *= k;
      }
      if (bounds) bounds.expandByPoint(v.clone().applyMatrix4(toRootOnly));
      v.applyMatrix4(full); pos[o * 3] = v.x; pos[o * 3 + 1] = v.y; pos[o * 3 + 2] = v.z;
      if (N) { n.applyMatrix3(n3).normalize(); nrm[o * 3] = n.x; nrm[o * 3 + 1] = n.y; nrm[o * 3 + 2] = n.z; }
      if (U) { uv[o * 2] = U.getX(i); uv[o * 2 + 1] = U.getY(i); }
      pivot[o * 3] = piv.x; pivot[o * 3 + 1] = piv.y; pivot[o * 3 + 2] = piv.z;
      up[o * 3] = useUp.x; up[o * 3 + 1] = useUp.y; up[o * 3 + 2] = useUp.z;
      out[o * 3] = useOut.x; out[o * 3 + 1] = useOut.y; out[o * 3 + 2] = useOut.z;
      data[o * 2] = p.row + p.copy * COPY_STRIDE; data[o * 2 + 1] = W ? W.getX(i) : 0;
      skinIndex[o * 4] = p.mount.index; skinWeight[o * 4] = 255;
    }
    const I = g.index!;
    for (let i = 0; i < I.count; i++) index[at++] = I.getX(i) + base;
    base += P.count;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.setAttribute("normal", new THREE.BufferAttribute(nrm, 3));
  geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
  geo.setAttribute("kitPivot", new THREE.BufferAttribute(pivot, 3));
  geo.setAttribute("kitUp", new THREE.BufferAttribute(up, 3));
  geo.setAttribute("kitOut", new THREE.BufferAttribute(out, 3));
  geo.setAttribute("kitData", new THREE.BufferAttribute(data, 2));
  geo.setAttribute("skinIndex", new THREE.BufferAttribute(skinIndex, 4));
  geo.setAttribute("skinWeight", new THREE.BufferAttribute(skinWeight, 4, true));
  geo.setIndex(new THREE.BufferAttribute(index, 1));
  geo.name = "skin-kit";
  return geo;
}
